'use client';

import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SellProperty } from '@/features/components/SellProperty';

import {
  FiltersType,
  SearchFilterStructureRo,
  OfferType,
  OfferTypeObject,
  ReferenceLocation,
  SearchTabs,
} from '@/types';
import { useGetSearchProfile, useGetHousingList, useResolveLocations } from '@/queris';

import { AUSTRIA_COUNTRY_CODE, MAX_FILTERS_RANGE_VALUES, PATHS } from '@/constants';
import { buildSearchObj } from './utils/buildSearchObj';
import { getFiltersFromProfileSearch, isOnlyLand } from '@/common';
import {
  useFilters,
  useDebounce,
  useHousingInitialFilters,
  useHousingListFilterInteractionAnalytics,
  useMapFilters,
  useScrollToCard,
} from '@/hooks';

import { Container, MainBlock, Wrapper } from './styles';

import { Content, FeaturedCards, Head, LeftMenu } from './components';
import {
  pushListSearchView,
  pushMapObjectSearchStart,
  pushMapSearchView,
  stripFilterLabelTitle,
} from '@/lib/analytics';
import { useAppContext } from '@/contexts';
import { useSearchParamsCompat } from '@/hooks';
import { useRouter } from 'next/navigation';
import { usePathname } from 'next/navigation';
import type {
  MapFilterData,
  MapSearchResultEvent,
  MapSearchStatusEvent,
  MapWidgetState,
} from '@propileya/geo-widget';

export const LOCAL_FILTERS = 'local-search-profile-filters';

const PROJECT_EQUIPMENT_EXCLUDE = new Set([
  'oldBuilding.true',
  'newBuilding.true',
  'firstUsage.true',
]);

const PROJECT_FILTER_DEFAULTS = { unitListValue: [AUSTRIA_COUNTRY_CODE] };

type HousingListScreenProps = {
  initialFiltersData: SearchFilterStructureRo | null;
  isProject?: boolean;
  predefinedFilters?: Partial<FiltersType>;
};

export const HousingListScreen: FC<HousingListScreenProps> = ({
  initialFiltersData,
  isProject = false,
  predefinedFilters,
}) => {
  const searchParams = useSearchParamsCompat();

  const searchProfileId = searchParams.get('search-profile');
  const fromDetailId = searchParams.get('fromDetailId');
  const officeIdParam = searchParams.get('officeId');

  const { setObjectList } = useAppContext();

  const { data: profileSearch, isLoading: isProfileSearchLoading } =
    useGetSearchProfile(searchProfileId);
  const router = useRouter();
  const pathname = usePathname();
  const isDirectLink = pathname.includes(`/${PATHS.DIRECTLINK}/`);

  const { filters, resetFilters, getResetUrl, canReset, setFilters, setItemsPerPage } = useFilters(
    isProject
      ? { options: { defaults: PROJECT_FILTER_DEFAULTS } }
      : predefinedFilters
        ? { options: { initialFilters: predefinedFilters } }
        : undefined,
  );

  const {
    offerTypeList,
    categoriesMenuList,
    equipmentMenuList,
    unitList,
    rangesDimensions,
    powerConsumptionClassesList,
    energyClassFactorsList,
    isFiltersLoading,
    isFiltersReady,
    setCountersData,
  } = useHousingInitialFilters(initialFiltersData);

  const activeEquipmentMenuList = useMemo(
    () =>
      isProject
        ? equipmentMenuList.filter((e) => !PROJECT_EQUIPMENT_EXCLUDE.has(e.code))
        : equipmentMenuList,
    [isProject, equipmentMenuList],
  );

  const [locations, setLocations] = useState<ReferenceLocation[]>(filters.locations ?? []);

  useEffect(() => {
    setLocations(filters.locations ?? []);
  }, [filters.locations]);

  // On first render, resolve URL-parsed location stubs (type/code/name only) into fully-enriched
  // objects (admunitIds, lat/lng). The query is disabled once admunitIds are already present
  // (i.e. the user picked from the autocomplete, which returns full data).
  const { data: resolvedLocations } = useResolveLocations(filters.locations);
  const resolveAppliedRef = useRef(false);
  useEffect(() => {
    if (resolvedLocations?.length && !resolveAppliedRef.current) {
      resolveAppliedRef.current = true;
      setLocations(resolvedLocations);
    }
  }, [resolvedLocations]); // intentional: runs once when resolve completes

  // Enrich code-only stubs from URL with title/count from menu (UI only, not written back to URL)
  const predefinedAdmunitNames = useMemo(() => {
    if (!predefinedFilters?.admunits?.length) return null;
    return new Map(predefinedFilters.admunits.map((a) => [a.id, a]));
  }, [predefinedFilters]);

  const enrichedFilters = useMemo(
    () => ({
      ...filters,
      categoriesValue: filters.categoriesValue.map(
        (c) => categoriesMenuList.find((m) => m.code === c.code) ?? c,
      ),
      equipmentValue: filters.equipmentValue
        .filter((e) => !isProject || !PROJECT_EQUIPMENT_EXCLUDE.has(e.code))
        .map((e) => activeEquipmentMenuList.find((m) => m.code === e.code) ?? e),
      admunits: filters.admunits?.map((a) =>
        a.name ? a : (predefinedAdmunitNames?.get(a.id) ?? a),
      ),
    }),
    [filters, categoriesMenuList, activeEquipmentMenuList, isProject, predefinedAdmunitNames],
  );

  const isLand = useMemo(() => isOnlyLand(filters.categoriesValue), [filters.categoriesValue]);

  const isProfileReady = searchProfileId ? !isProfileSearchLoading : true;

  const { preparedFilters, requestFilters } = useMemo(() => {
    const result = buildSearchObj(filters, initialFiltersData, {
      officeId: officeIdParam ? Number(officeIdParam) : undefined,
      isProject,
    });

    return {
      preparedFilters: result?.preparedFilters ?? filters,
      requestFilters: isProfileReady ? (result?.searchObj ?? undefined) : undefined,
    };
  }, [isProfileReady, filters, initialFiltersData, officeIdParam, isProject]);

  const showMap = filters.tabs === SearchTabs.MAP;

  useHousingListFilterInteractionAnalytics({
    isProfileReady,
    isFiltersReady,
    requestFilters,
    filters,
    preparedFilters,
    unitList,
    searchSource: showMap ? 'map' : 'list',
  });

  const requestFiltersKey = JSON.stringify(requestFilters);
  const debouncedRequestFilters = useDebounce(requestFilters, 600);
  const debouncedRequestFiltersKey = JSON.stringify(debouncedRequestFilters);
  const isDebouncing = requestFiltersKey !== debouncedRequestFiltersKey;

  // Filters without mapArea — used for the counters query on MAP tab.
  // Stripping mapArea means a pan/zoom of the map does NOT re-fire the counters request.
  const countersRequestFilters = useMemo(() => {
    if (!requestFilters) return requestFilters;
    const { mapArea: _mapArea, ...rest } = requestFilters as Record<string, unknown> & {
      mapArea?: unknown;
    };
    return rest as typeof requestFilters;
  }, [requestFilters]);
  const debouncedCountersFilters = useDebounce(countersRequestFilters, 600);

  useEffect(() => {
    if (showMap) pushMapSearchView();
    else if (!isProject) pushListSearchView();
  }, [isProject, showMap]);

  // Always run the list query — on MAP it uses counters filters (no mapArea) to update filter badges.
  const listEnabled = isProfileReady && isFiltersReady && !isDebouncing;

  const {
    data: queryData,
    isLoading,
    isFetching,
    isPending: isDataPending,
  } = useGetHousingList(showMap ? debouncedCountersFilters : debouncedRequestFilters, {
    enabled: listEnabled,
    trackAsSearch: rangesDimensions.price.length === 2 && !showMap,
    searchSource: 'list',
    searchStartCategoryTitles: enrichedFilters.categoriesValue.map(
      (c) => stripFilterLabelTitle(c.title) || c.code,
    ),
  });

  const data = queryData ?? undefined;

  // On MAP + office page we need officeName from objects, but the counters query returns no objects.
  // Fire a separate query with the full filters (including mapArea) just to get officeName.
  const { data: officeMapData } = useGetHousingList(debouncedRequestFilters, {
    enabled: !!(showMap && officeIdParam && listEnabled),
    queryKey: ['housing-list-office-map', debouncedRequestFilters],
  });

  const activeMapRequestRef = useRef<number | null>(null);
  const mapSearchSigRef = useRef<string | null>(null);

  const handleMapSearchStatus = useCallback(
    (event: MapSearchStatusEvent) => {
      if (event.status === 'loading') {
        activeMapRequestRef.current = event.requestId;
        if (rangesDimensions.price.length === 2) {
          pushMapObjectSearchStart(
            mapSearchSigRef,
            debouncedRequestFilters,
            enrichedFilters.categoriesValue
              .map((c) => stripFilterLabelTitle(c.title) || c.code)
              .filter(Boolean)
              .join(', '),
          );
        }
      }
    },
    [debouncedRequestFilters, enrichedFilters.categoriesValue, rangesDimensions.price.length],
  );

  const handleMapSearchResult = useCallback((_event: MapSearchResultEvent) => {}, []);

  // Reset map state when leaving map view.
  useEffect(() => {
    if (!showMap) {
      activeMapRequestRef.current = null;
      mapSearchSigRef.current = null;
    }
  }, [showMap]);

  const {
    mapFilters,
    mapSelection,
    mapInitialState,
    handleReady,
    handleStateChange,
    handleGoToMap,
  } = useMapFilters({
    filters,
    requestFilters: debouncedRequestFilters as Record<string, unknown> | undefined,
    locations,
    filterData: data?.filterData,
    setFilters,
  });

  // Map state — needed for zone names in Head.
  const [mapWidgetState, setMapWidgetState] = useState<MapWidgetState | null>(null);
  const handleMapReady = useCallback(
    (state: MapWidgetState) => {
      setMapWidgetState(state);
      handleReady(state);
    },
    [handleReady],
  );
  const handleMapStateChange = useCallback(
    (event: import('@propileya/geo-widget').MapStateChangeEvent) => {
      setMapWidgetState(event.state);
      handleStateChange(event);
    },
    [handleStateChange],
  );

  // Apply counters from list query in all modes.
  // setFilters(admunits) and setObjectList only run on LIST/TILE — on MAP objects are empty anyway.
  useEffect(() => {
    if (data) {
      setCountersData(data.filterData as MapFilterData);
      if (!showMap) {
        if (data.filterData.admunits?.length) {
          setFilters({
            admunits: data.filterData.admunits.map(({ id, name, parentName }) => ({
              id,
              name,
              parent_name: parentName,
            })),
          });
        }
      }
    }
    if (!showMap) {
      setObjectList(data?.objects || []);
    }
  }, [data, showMap, setCountersData, setFilters, setObjectList]);

  useEffect(() => {
    if (profileSearch?.filters) {
      setObjectList([]);
      setFilters({
        ...getFiltersFromProfileSearch(profileSearch.filters),
        tabs: profileSearch.viewType,
      });
    }
  }, [profileSearch?.filters, setFilters, setObjectList]);

  useScrollToCard({
    cardId: fromDetailId,
    hasData: !!data?.objects,
    isLoading: isLoading || isFetching || isDataPending,
  });

  const isMenuLoading = isFiltersLoading || isProfileSearchLoading;

  const isPending = isLoading || isFiltersLoading || isDataPending || isProfileSearchLoading;
  const isContentLoading = isPending || isFetching || isDebouncing;

  // Summary total always comes from the counters query (no mapArea) — shows how many objects match
  // the current filters across all of Austria, regardless of map viewport.
  const summaryTotal = data?.numFound;
  const summaryFetching = isFetching || isDebouncing;

  const chosenOfferType = offerTypeList.filter(
    (e: { code: OfferType }) => e.code === filters.offerTypeValue,
  );

  const handleChangeOfferTypeValue = useCallback(
    (value: OfferTypeObject[]) => {
      if (value.length === 0) return;

      const lastValue = value[value.length - 1];
      setFilters({ offerTypeValue: lastValue.code, currentPage: 0 });
    },
    [setFilters],
  );

  const handleLocationsChange = (newLocations: ReferenceLocation[]) => {
    setLocations(newLocations);
    setFilters({ locations: newLocations, currentPage: 0 });
  };

  const handleFullResetFilters = () => {
    setLocations([]);
    if (isDirectLink) {
      router.push(getResetUrl(`/${PATHS.HOUSING}`));
    } else {
      resetFilters();
    }
  };

  const officeName = useMemo(() => {
    if (!officeIdParam) return '';
    const source = showMap ? officeMapData : data;
    return source?.objects?.[0]?.brokerData?.officeName || '';
  }, [officeIdParam, showMap, officeMapData, data]);

  const excludedIds = useMemo(() => data?.objects?.map((item) => item.id), [data]);

  // Build a summary data object for Head — it only needs numFound.
  const headData = useMemo(
    () => (summaryTotal !== undefined ? { numFound: summaryTotal } : undefined),
    [summaryTotal],
  );

  return (
    <Wrapper>
      <Container>
        <Head
          preparedFilters={preparedFilters}
          data={summaryFetching ? undefined : headData}
          summaryTotal={summaryFetching ? undefined : summaryTotal}
          filters={enrichedFilters}
          locations={locations}
          onLocationsChange={handleLocationsChange}
          tabs={filters.tabs ?? SearchTabs.LIST}
          isPending={isPending}
          isFetching={summaryFetching}
          onChangeValue={setFilters}
          onGoToMap={handleGoToMap}
          unitList={unitList}
          offerTypeList={offerTypeList}
          chosenOfferType={chosenOfferType}
          onChangeOfferTypeValue={handleChangeOfferTypeValue}
          rangesDimensions={rangesDimensions}
          powerConsumptionClassesList={powerConsumptionClassesList}
          energyClassFactorsList={energyClassFactorsList}
          isLand={isLand}
          onFullResetFilters={handleFullResetFilters}
          canReset={canReset}
          categoriesMenuList={categoriesMenuList}
          equipmentMenuList={activeEquipmentMenuList}
          officeName={officeName}
          isProject={isProject}
          mapSelectedAreas={mapWidgetState?.selectedAreas}
        />
        <MainBlock $isMap={filters.tabs === SearchTabs.MAP}>
          {(filters.tabs === SearchTabs.LIST || filters.tabs === SearchTabs.MAP) && (
            <LeftMenu
              viewType={filters.tabs}
              isLoading={isMenuLoading}
              filters={preparedFilters}
              isProject={isProject}
              onFullResetFilters={handleFullResetFilters}
              canReset={canReset}
              onGoToMap={handleGoToMap}
              onClearMapFilters={() =>
                setFilters({ mapArea: undefined, admunits: undefined, currentPage: 0 })
              }
              categories={categoriesMenuList}
              categoriesValue={enrichedFilters.categoriesValue}
              setCategoriesValue={(categories) =>
                setFilters({ categoriesValue: categories, currentPage: 0 })
              }
              equipment={activeEquipmentMenuList}
              equipmentValue={enrichedFilters.equipmentValue}
              setEquipmentValue={(equipmentList) =>
                setFilters({ equipmentValue: equipmentList, currentPage: 0 })
              }
              unitList={unitList}
              unitListValue={filters.unitListValue}
              setUnitListValue={(value) => setFilters({ unitListValue: value, currentPage: 0 })}
              rangesDimensions={{
                ...rangesDimensions,
                area: [
                  rangesDimensions.area[0],
                  isLand ? MAX_FILTERS_RANGE_VALUES.LAND_AREA : MAX_FILTERS_RANGE_VALUES.AREA,
                ],
              }}
              roomNumberValue={filters.roomNumberValue}
              setRoomNumberValue={(value) => setFilters({ roomNumberValue: value, currentPage: 0 })}
              priceValue={filters.priceValue}
              setPriceValue={(value) => setFilters({ priceValue: value, currentPage: 0 })}
              rentPriceValue={filters.rentPriceValue}
              setRentPriceValue={(value) => setFilters({ rentPriceValue: value, currentPage: 0 })}
              areaValue={filters.areaValue}
              setAreaValue={(value) => setFilters({ areaValue: value, currentPage: 0 })}
              powerConsumptionClassesList={powerConsumptionClassesList}
              powerConsumptionClasses={filters.powerConsumptionClasses}
              setPowerConsumptionClasses={(value) =>
                setFilters({ powerConsumptionClasses: value, currentPage: 0 })
              }
              energyClassFactorsList={energyClassFactorsList}
              energyClassFactors={filters.energyClassFactors}
              setEnergyClassFactors={(value) =>
                setFilters({ energyClassFactors: value, currentPage: 0 })
              }
              offerTypeList={offerTypeList}
              offerTypeValue={filters.offerTypeValue}
              setOfferTypeValue={handleChangeOfferTypeValue}
              locations={locations}
              onLocationsChange={handleLocationsChange}
            />
          )}
          <Content
            offerType={filters.offerTypeValue}
            view={filters.tabs}
            objects={isContentLoading ? undefined : data?.objects}
            isLoading={isContentLoading}
            page={filters.currentPage}
            numFound={data?.numFound}
            itemsPerPage={filters.itemsPerPage}
            onPageChange={(value) => {
              window.scrollTo({ top: 0 });
              setFilters({ currentPage: value });
            }}
            onItemsPerPageChange={(value) => setItemsPerPage(value)}
            priceValue={preparedFilters.priceValue}
            rentPriceValue={preparedFilters.rentPriceValue}
            sorting={filters.sorting}
            isProject={isProject}
            mapFilters={showMap ? mapFilters : undefined}
            mapSelection={showMap ? mapSelection : undefined}
            mapInitialState={showMap ? mapInitialState : undefined}
            onMapReady={handleMapReady}
            onMapStateChange={handleMapStateChange}
            onMapSearchResult={handleMapSearchResult}
            onMapSearchStatusChange={handleMapSearchStatus}
          />
        </MainBlock>
      </Container>

      {!isProject && <FeaturedCards filters={debouncedRequestFilters} excludedIds={excludedIds} />}
      <SellProperty />
    </Wrapper>
  );
};
